"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.cancelPendingAggregations = exports.queueAggregationRequest = exports.config = void 0;
const tslib_1 = require("tslib");
const groupBy_1 = tslib_1.__importDefault(require("lodash/groupBy"));
const map_1 = tslib_1.__importDefault(require("lodash/map"));
const difference_1 = tslib_1.__importDefault(require("lodash/difference"));
const keys_1 = tslib_1.__importDefault(require("lodash/keys"));
const fromPairs_1 = tslib_1.__importDefault(require("lodash/fromPairs"));
const size_1 = tslib_1.__importDefault(require("lodash/size"));
const get_1 = tslib_1.__importDefault(require("lodash/get"));
const has_1 = tslib_1.__importDefault(require("lodash/has"));
const debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
const head_1 = tslib_1.__importDefault(require("lodash/head"));
const mapValues_1 = tslib_1.__importDefault(require("lodash/mapValues"));
const each_1 = tslib_1.__importDefault(require("lodash/each"));
const filter_1 = tslib_1.__importDefault(require("lodash/filter"));
const rxjs_1 = require("rxjs");
const uuid_1 = require("uuid");
const loading_states_const_1 = require("../../constants/loading-states.const");
const request_utils_1 = require("./request.utils");
// all in-flight aggs will be in this object until they are resolved or rejected
const pendingRequests = {};
const batch = [];
exports.config = {
    pendingRequests,
    batch,
    returnRequest: false,
    isMAULimitingDisabled: getItem('aggregation-disabledMAULimiting') === 'true',
    isCachingDisabled: getItem('aggregation-disabledCaching') === 'true',
    masterTaskCost: getItem('aggregation-masterTaskCost'),
    areRequestsBundled: getItem('aggregation-requestBundling') === 'true',
    maxCsvAttempts: 600,
    maxAttempts: 5,
    disableTimingIds: getItem('aggregation-disableTimingIds') === 'true',
    addAggNamesToRequestUrls: getItem('aggregation-addAggNamesToRequestUrls') === 'true'
};
// slightly-hidden interface to allow devs to fiddle with aggs via the console
window[Symbol.for('Aggregation')] = {
    disableCaching,
    enableCaching,
    disableMAULimiting,
    enableMAULimiting,
    enableAggIdentifierParams,
    disableAggIdentifierParams,
    setMasterTaskCost,
    resetMasterTaskCost,
    enableRequestBundling,
    disableRequestBundling,
    enableRequestDebouncing,
    disableRequestDebouncing,
    enableTimingIds,
    disableTimingIds,
    enableNamesOnRequestUrls,
    disableNamesOnRequestUrls,
    config: exports.config,
    queue: queueAggregationRequest
};
const debouncedFetchBatchedAggregations = (0, debounce_1.default)(fetchBatchedAggregations, 25);
const loadingStatus = Object.freeze({ status: loading_states_const_1.LOADING_STATES.LOADING });
function queueAggregationRequest(agg, aggConfig = {}) {
    const request = addAggregationToBatch(agg, exports.config);
    if (exports.config.areRequestsBundled || exports.config.alwaysDebounceRequests || aggConfig.debounceRequests) {
        debouncedFetchBatchedAggregations(aggConfig);
    }
    else {
        fetchBatchedAggregations(aggConfig);
    }
    return exports.config.returnRequest ? request : request.subject;
}
exports.queueAggregationRequest = queueAggregationRequest;
function cancelPendingAggregations(excludedAggIds = []) {
    const requestsToCancel = { ...pendingRequests }; // requests to cancel lookup from global pending request object
    if (excludedAggIds.length) {
        const activeAggs = Object.getOwnPropertyNames(pendingRequests);
        excludedAggIds.forEach((excludedAggId) => {
            activeAggs.forEach((agg) => {
                if (agg.includes(excludedAggId)) {
                    delete requestsToCancel[agg];
                }
            });
        });
    }
    (0, each_1.default)(requestsToCancel, (request) => {
        if (request.cancellable) {
            if (request.type === 'report') {
                softCancelRequest(request);
            }
            else {
                cancelRequest(request);
            }
        }
    });
}
exports.cancelPendingAggregations = cancelPendingAggregations;
function fetchBatchedAggregations(aggConfig) {
    const requests = batch.filter((request) => !request.cancelled);
    // prettier-ignore
    const { adhoc: adhocRequests = [], report: reportRequests = [], adhocCsv: adhocCsvRequests = [], reportCsv: reportCsvRequests = [] } = (0, groupBy_1.default)(requests, (request) => request.type);
    fetchAdhocAggregations(adhocRequests, aggConfig);
    fetchReport(reportRequests);
    fetchCsv(adhocCsvRequests);
    fetchCsv(reportCsvRequests);
    batch.length = 0;
}
function addAggregationToBatch(aggregation) {
    const request = createRequest(aggregation);
    cancelRelevantPendingRequests(request);
    batch.push(request);
    pendingRequests[request.requestId] = request;
    return request;
}
async function fetchAggregations({ requests, responseConfig, attempts = 1, aggConfig = {} }) {
    if (requests.length === 0)
        return;
    const aggs = requests.map((request) => request.aggregation);
    const aggRequest = sendAggregationRequest({ aggs, aggConfig, responseConfig });
    requests.forEach((request) => {
        request.stats.attempts = attempts;
        request.stats.dequeued = request.stats.dequeued || now();
        request.promises.aggregation = aggRequest;
    });
    try {
        const response = await aggRequest;
        throwIfNoContent(response);
        const { data: { periods = {}, jobId, aggregationJobId } = {} } = response;
        // adds `periods` to request objects so we can know when an agg is "complete"
        addPeriodsToRequests(requests, periods);
        requests.forEach((request) => {
            if (request.cancelled)
                return;
            request.jobId = aggregationJobId || jobId;
        });
        return response;
    }
    catch (error) {
        if (aggConfig && aggConfig.aggTimeout) {
            throw error;
        }
        throwIfFailed(error, attempts);
        return fetchAggregations({ requests, responseConfig, attempts: attempts + 1, aggConfig });
    }
}
async function fetchAdhocAggregations(requests, aggConfig) {
    if (!requests.length)
        return;
    // all aggs here have same `responseConfig` (the default)
    // and we send them together in a single request
    const { responseConfig } = (0, head_1.default)(requests);
    try {
        let timingId;
        if (!exports.config.disableTimingIds) {
            timingId = generateTimingId();
            addTimingIdsToRequests(requests, timingId);
        }
        // At this point, we are sending a single agg API call containing *all* of the requests.
        const response = await fetchAggregations({
            requests,
            responseConfig,
            aggConfig: { ...aggConfig, timingId }
        });
        // resolution of aggs is done in `handleAdhocResponse` or child functions
        await handleAdhocResponse(response, requests);
        // insurance against showing "incomplete" data to users
        verifyCompletenessOfBatchedAggregations(requests);
    }
    catch (error) {
        requests.forEach((request) => reject(request, error));
    }
}
function fetchCsv(requests) {
    requests.forEach(async (request) => {
        const { responseConfig } = request;
        try {
            await fetchAggregations({ requests: [request], responseConfig });
            const url = await fetchUrlOfCompletedCsv(request, responseConfig.name);
            resolve(request, url);
        }
        catch (error) {
            reject(request, error);
        }
    });
}
function fetchReport(requests) {
    requests.forEach(async (request) => {
        const { report, forceRun, impersonating } = request;
        const { currentRunningAgg, lastSuccessRunObj, lastSuccessRunAt } = getReportControlFields(report, request);
        const isWorkflowReportMultiAgg = (0, get_1.default)(request, 'requestId', '').includes('WorkflowReportDetails');
        try {
            let resolvedData;
            if (forceRun) {
                resolvedData = await runReport(request);
            }
            else if (currentRunningAgg) {
                request.jobId = currentRunningAgg;
                resolvedData = await waitForReportCompletion(request);
            }
            else if (lastSuccessRunObj || (isWorkflowReportMultiAgg && lastSuccessRunAt)) {
                resolvedData = await fetchReportDataFromCacheWithFallback(request);
            }
            else {
                if (impersonating) {
                    await wait(1000);
                    throw new Error('Impersonated users must forcefully run reports.');
                }
                resolvedData = await runReport(request);
            }
            const { report, data } = resolvedData;
            resolve(request, {
                report,
                data
            });
        }
        catch (error) {
            reject(request, error);
        }
    });
}
async function runReport(request) {
    const { responseConfig } = request;
    await fetchAggregations({ requests: [request], responseConfig });
    return waitForReportCompletion(request);
}
async function fetchReportDataFromCacheWithFallback(request) {
    const { requestId, report, impersonating } = request;
    const { lastSuccessRunObj } = getReportControlFields(report, request);
    const isWorkflowReportMultiAgg = (0, get_1.default)(request, 'requestId', '').includes('WorkflowReportDetails');
    try {
        const data = isWorkflowReportMultiAgg ? await (0, request_utils_1.getCompiledResults)(request.report.id) : await fetchFileFromGCS(lastSuccessRunObj, requestId);
        return { report, data };
    }
    catch (err) {
        if (impersonating)
            throw err;
        return runReport(request);
    }
}
// this pings the `status` endpoint for an hour, assuming no errors and no cancellation
// until `percentComplete === 100`, then it fetches the url for the file in gcs
// then it fetches the file in gcs with the url and resolves
async function waitForReportCompletion(request, attempts = 1, start = Date.now()) {
    throwCancelledRequest(request);
    throwStuckRequest(request, start);
    try {
        const { data: { percentComplete } } = await (0, request_utils_1.getStatus)(request.jobId);
        if (percentComplete === 100) {
            notify(request, 100);
            const { data: updatedReport } = await (0, request_utils_1.getReportResource)(request.report.id, (0, get_1.default)(request, 'report.kind', ''));
            const { lastSuccessRunObj, currentRunningAgg } = getReportControlFields(updatedReport, request);
            if (currentRunningAgg) {
                request.jobId = currentRunningAgg;
                return waitForReportCompletion(request, attempts, start);
            }
            const isWorkflowReportMultiAgg = (0, get_1.default)(request, 'requestId', '').includes('WorkflowReportDetails');
            if (!lastSuccessRunObj && !isWorkflowReportMultiAgg) {
                throw new Error('Missing `lastSuccessRunObj` in completed report.');
            }
            const data = isWorkflowReportMultiAgg ? await (0, request_utils_1.getCompiledResults)(request.report.id) : await fetchFileFromGCS(lastSuccessRunObj, request.requestId);
            return { report: updatedReport, data };
        }
        notify(request, scaleProgress(percentComplete));
        await wait(1000);
        return waitForReportCompletion(request, attempts, start);
    }
    catch (error) {
        throwIfTooManyAttempts(error, attempts);
        await wait(1000);
        return waitForReportCompletion(request, attempts + 1, start);
    }
}
// fetches an ever-changing url for the request, checks gcs for presence of data
// if the url's request resolves, then it resolves with its url so the user can download the file.
// yes, this will intentionally 404 until the file is present in GCS
async function fetchUrlOfCompletedCsv(request, requestName, attempts = 1) {
    throwCancelledRequest(request);
    try {
        const { data: { url } } = await fetchObjectUrl(requestName);
        const { data: { error } } = await fetchFromGCS(url);
        if (error) {
            throw error;
        }
        return url;
    }
    catch (error) {
        if (attempts < exports.config.maxCsvAttempts) {
            await waitWithProgressiveBackoff(attempts);
            return fetchUrlOfCompletedCsv(request, requestName, attempts + 1);
        }
        throw error;
    }
}
async function fetchFileFromGCS(requestName, requestId) {
    const { data: { url } } = await fetchObjectUrl(requestName);
    const { data } = await fetchFromGCS(url);
    if (data.error) {
        throw data.error;
    }
    const dataByRequestId = groupNewDataByRequestId(data);
    return dataByRequestId[requestId];
}
// make it clear to users that "work is happening" by starting at 5% if the agg made it this far
function scaleProgress(percent) {
    return Math.floor(5 + 95 * (percent * 0.01));
}
// since adhoc CSVs hammer endpoints, we slowly make the time btwn requests longer
function waitWithProgressiveBackoff(attempts) {
    let timeout = 1000;
    if (attempts > 10 && attempts < 30) {
        timeout *= 2.5;
    }
    else if (attempts > 31 && attempts < 60) {
        timeout *= 5;
    }
    else if (attempts > 61) {
        timeout *= 10;
    }
    return wait(timeout);
}
function wait(time) {
    return new Promise((resolve) => {
        setTimeout(resolve, time);
    });
}
// a "catch-all" function for adhoc agg responses
// 1. processes + resolves aggs if all their data is present in the agg response
// 2. sends requests for messages that have data stored in at an endpoint
// 3. sends `continueUrl` request if it exists
async function handleAdhocResponse(response, requests) {
    const { data: { messages, continueUri } = {} } = response;
    // The requests array could be longer than even the unfiltered messages array,
    // e.g. if `handleAdhocResponse` was called from `fetchResultsFromContinueUri`.
    addRowsToRequest(requests, (0, filter_1.default)(messages, (msg) => (0, has_1.default)(msg, 'rows')));
    const gcsPromises = (0, map_1.default)((0, filter_1.default)(messages, (msg) => (0, has_1.default)(msg, 'url')), (message) => fetchResultsFromMessageUrl(message));
    const continueUriPromise = fetchResultsFromContinueUri(continueUri, requests);
    return Promise.all([...gcsPromises, continueUriPromise]);
}
// these URLs are associated with specific rows in an agg
// we send the request here and also add the rows/resolve aggs that are completed
async function fetchResultsFromMessageUrl(message, attempts = 1) {
    const { url, requestId, time } = message;
    const request = pendingRequests[requestId];
    if (!request)
        return;
    try {
        let timingId;
        if (!exports.config.disableTimingIds) {
            timingId = generateTimingId();
            addTimingIdsToRequests([request], timingId);
        }
        const requestPromise = fetchFromGCS(url, {
            params: {
                timingId
            }
        });
        const response = await requestPromise;
        throwIfNoContent(response);
        addRowsToRequest([request], [{ requestId, time, rows: response.data }]);
    }
    catch (error) {
        throwIfTooManyAttempts(error, attempts);
        return fetchResultsFromMessageUrl(message, attempts + 1);
    }
}
// the response from the `continueUri` endpoint is shaped like the response from the agg endpoint
// so re-call `handleAdhocResponse`
async function fetchResultsFromContinueUri(continueUri, requests, attempts = 1) {
    if (!continueUri)
        return;
    try {
        let timingId;
        if (!exports.config.disableTimingIds) {
            timingId = generateTimingId();
            addTimingIdsToRequests(requests, timingId);
        }
        const requestPromise = (0, request_utils_1.getContinueUri)(continueUri, {
            params: {
                timingId
            }
        });
        const response = await requestPromise;
        throwIfNoContent(response);
        return handleAdhocResponse(response, requests);
    }
    catch (error) {
        throwIfTooManyAttempts(error, attempts);
        return fetchResultsFromContinueUri(continueUri, requests, attempts + 1);
    }
}
function throwIfNoContent(response) {
    const status = (0, get_1.default)(response, 'status');
    if (status === 204) {
        const error = new Error('cancelled');
        error.response = response;
        throw error;
    }
}
function throwIfFailed(error, attempts) {
    const status = (0, get_1.default)(error, ['response', 'status']);
    if (status === 502 || status === 503) {
        throwIfTooManyAttempts(error, attempts);
    }
    else {
        throw error;
    }
}
function throwIfTooManyAttempts(error, attempts) {
    if (attempts >= exports.config.maxAttempts) {
        throw error;
    }
}
function throwCancelledRequest(request) {
    if (request.cancelled)
        throw new Error('cancelled');
}
function throwStuckRequest(request, start) {
    const elapsed = Date.now() - start;
    const hourInMs = 3600000;
    if (elapsed > hourInMs)
        throw new Error('Report aggregation failed to complete within an hour.');
}
// insurance against showing "incomplete" data to users
// since "adhoc" aggs can trigger a lot of separate requests, especially for larger, slower subs
// if this error message is triggered it is 99.99% a FE race condition bug
function verifyCompletenessOfBatchedAggregations(requests) {
    requests.forEach((request) => {
        const { cancelled, periods, data, requestId, jobId } = request;
        if (cancelled)
            return;
        const stringifiedPeriods = (0, map_1.default)(periods, String);
        const missingPeriods = (0, difference_1.default)(stringifiedPeriods, (0, keys_1.default)(data));
        if (missingPeriods.length) {
            reject(request, new Error('incomplete'));
            // eslint-disable-next-line no-console
            console.error(`Job ${jobId} finished, but request ${requestId} is missing period(s): ${missingPeriods.join(', ')}`);
        }
    });
}
// add `data` from messages to `request.data` and if the
// number of keys in `request.data` is equal to the number of periods
// we received from the initial agg request then the agg is complete
function addRowsToRequest(requests, messages) {
    if (!messages)
        return;
    const dataByRequestId = groupNewDataByRequestId(messages);
    requests.filter((request) => {
        const { requestId } = request;
        const dataFromMessages = dataByRequestId[requestId];
        if (!dataFromMessages || request.cancelled)
            return false;
        request.data = { ...request.data, ...dataFromMessages.data };
        const numResolvedPeriods = (0, size_1.default)(request.data);
        if (numResolvedPeriods === request.periods.length) {
            const sortedData = (0, keys_1.default)(request.data)
                .sort()
                .map((period) => [period, request.data[period]]);
            resolve(request, {
                id: requestId,
                data: request.data,
                periods: request.periods.sort(),
                sortedData
            }, false);
            return true;
        }
        return false;
    });
}
function groupNewDataByRequestId(msgOrMsgs) {
    const messages = [].concat(msgOrMsgs);
    const msgsByRequestId = (0, groupBy_1.default)(messages, 'requestId');
    return (0, mapValues_1.default)(msgsByRequestId, (msgs, requestId) => {
        const data = (0, fromPairs_1.default)(msgs.map(({ time, rows }) => [time, rows]));
        const periods = (0, map_1.default)(msgs, 'time');
        return {
            requestId,
            data,
            periods
        };
    });
}
function addPeriodsToRequests(requests, periodMap) {
    requests.forEach((request) => {
        const periods = periodMap[request.requestId];
        if (periods) {
            request.periods = periods;
        }
    });
}
// `adhoc`, `adhocCsv`, `report`, `reportCsv`
// each "type" is sent differently
function getAggregationType(isReport, response) {
    const { mimeType } = response || {};
    const isCsv = mimeType === 'text/csv';
    let type = isReport ? 'report' : 'adhoc';
    if (isCsv) {
        type += 'Csv';
    }
    return type;
}
function isReportAgg(agg) {
    const location = (0, get_1.default)(agg, 'response.location');
    const reportLocations = ['report', 'workflow', 'journey', 'workflowvisitorlist', 'workflowbaselinecomparison'];
    return !!agg.requestId || reportLocations.includes(location);
}
function createRequest(agg) {
    const { response, name, productArea, subProductArea } = agg;
    const requestId = agg.requestId || agg.mockRequestId || `${name}-rId-${(0, uuid_1.v4)()}`;
    const isReport = isReportAgg(agg);
    const type = getAggregationType(isReport, response);
    const { report, forceRun, impersonating, ...responseConfig } = response || {
        location: 'request',
        mimeType: 'application/json'
    };
    if (type === 'adhocCsv') {
        responseConfig.name = `${requestId}.csv`;
    }
    if (agg.requests) {
        agg.requests = agg.requests.map((request) => Object.assign(request, { requestId: `${request.name}-rId-${(0, uuid_1.v4)()}` }));
    }
    const aggObject = {
        ...agg,
        requestId,
        response: undefined
    };
    if (!window.Cypress) {
        // ensure params are not sent as part of the request
        delete aggObject.params;
    }
    return {
        requestId,
        productArea,
        subProductArea,
        streamId: agg.streamId,
        aggregation: aggObject,
        type,
        promises: {
            aggregation: Promise.resolve()
        },
        // BehaviorSubjects will emit most recent event, starting with what it is initialized with
        // assuming the observable has not yet "completed"
        subject: new rxjs_1.BehaviorSubject(loadingStatus),
        responseConfig,
        data: {},
        stats: {
            attempts: 0,
            queued: now(),
            timingIds: new Set()
        },
        periods: [],
        cancellable: true,
        cancelled: false,
        ...{ report, forceRun, impersonating }
    };
}
function now() {
    return performance ? performance.now() : Date.now();
}
function cancelRelevantPendingRequests({ aggregation, streamId }) {
    // when aggs are bundled we can't cancel specific aggs
    if (exports.config.areRequestsBundled)
        return;
    if (streamId) {
        cancelByStreamId(streamId);
    }
    else {
        cancelByName(aggregation.name);
    }
}
// `streamId` denotes an agg request for a specific widget etc. so
// if a new aggregation request is triggered it is always safe to cancel
// any pending requests with the same streamId
function cancelByStreamId(sId) {
    (0, each_1.default)(pendingRequests, (request) => {
        if (request.streamId === sId) {
            cancelRequest(request);
        }
    });
}
// less safe, so we've disabled this on certain routes
// depends upon a unique aggregation `name`
function cancelByName(name) {
    const regex = new RegExp(`^${name}-rId-[\\w|-]{36}$`);
    (0, each_1.default)(pendingRequests, (request) => {
        if (regex.test(request.requestId)) {
            cancelRequest(request);
        }
    });
}
function softCancelRequest(request) {
    if (!request.cancellable)
        return;
    request.cancelled = true;
    reject(request, new Error('cancelled'));
}
async function cancelRequest(request) {
    if (!request.cancellable)
        return;
    request.cancelled = true;
    reject(request, new Error('cancelled'));
    let { jobId } = request;
    if (!jobId) {
        const aggregationResponse = await request.promises.aggregation;
        const { data = {} } = aggregationResponse || {};
        jobId = data.aggregationJobId || data.jobId;
    }
    (0, request_utils_1.deleteJob)(jobId);
}
function addTimingIdsToRequests(requests, timingId) {
    requests.forEach((request) => {
        request.stats.timingIds.add(timingId);
    });
}
function fetchFromGCS(url, requestConfig) {
    return (0, request_utils_1.getObjectWithUrl)(url, requestConfig);
}
function fetchObjectUrl(objectName) {
    return (0, request_utils_1.getObjectUrl)(objectName);
}
function sendAggregationRequest({ aggs, aggConfig = {}, responseConfig }) {
    const request = {
        response: responseConfig,
        requests: aggs[0].requests || aggs
    };
    const { isCachingDisabled, isMAULimitingDisabled, masterTaskCost, includeAggIdentifierParams } = exports.config;
    const { aggTimeout: timeout, timingId } = aggConfig;
    const requestContainsMultipleAggs = aggs.length > 1;
    const shouldAddNameToRequestUrl = exports.config.addAggNamesToRequestUrls && !requestContainsMultipleAggs;
    const requestConfig = {
        params: {
            mauLimitingBehavior: isMAULimitingDisabled ? 'ignore' : undefined,
            cachepolicy: isCachingDisabled ? 'all:ignore' : undefined,
            mtcost: masterTaskCost,
            ...(masterTaskCost ? { mt: true } : {}),
            requestid: includeAggIdentifierParams ? aggs.map(({ requestId }) => requestId).toString() : undefined,
            timingId
        },
        timeout
    };
    if (shouldAddNameToRequestUrl) {
        requestConfig.params.name = (0, get_1.default)(aggs, '[0].name');
    }
    return (0, request_utils_1.postAggregation)(request, requestConfig);
}
function notify(request, notification) {
    request.subject.next({
        status: loading_states_const_1.LOADING_STATES.NOTIFICATION,
        notification
    });
}
function reject(request, error) {
    request.subject.next({
        status: loading_states_const_1.LOADING_STATES.REJECTED,
        error
    });
    request.subject.complete();
    delete pendingRequests[request.requestId];
}
function resolve(request, data) {
    request.stats.complete = now();
    request.subject.next({
        status: loading_states_const_1.LOADING_STATES.RESOLVED,
        value: data
    });
    request.subject.complete();
    delete pendingRequests[request.requestId];
}
function enableAggIdentifierParams() {
    exports.config.includeAggIdentifierParams = true;
    return 'identifier params included. this will persist for the life of this session.';
}
function disableAggIdentifierParams() {
    exports.config.includeAggIdentifierParams = false;
    return 'identifier params disabled';
}
function disableCaching() {
    exports.config.isCachingDisabled = true;
    setItem('aggregation-disabledCaching', exports.config.isCachingDisabled);
    return 'caching disabled. this will persist for the life of this session.';
}
function enableCaching() {
    exports.config.isCachingDisabled = false;
    removeItem('aggregation-disabledCaching');
    return 'caching enabled';
}
function disableMAULimiting() {
    exports.config.isMAULimitingDisabled = true;
    setItem('aggregation-disabledMAULimiting', exports.config.isMAULimitingDisabled);
    return 'mau limiting disabled. this will persist for the life of this session.';
}
function enableMAULimiting() {
    exports.config.isMAULimitingDisabled = false;
    removeItem('aggregation-disabledMAULimiting');
    return 'mau limiting enabled';
}
function setMasterTaskCost(cost) {
    exports.config.masterTaskCost = cost;
    setItem('aggregation-masterTaskCost', exports.config.masterTaskCost);
    return `mtcost set to ${cost}. this will persist for the life of this session.`;
}
function resetMasterTaskCost() {
    exports.config.masterTaskCost = null;
    removeItem('aggregation-masterTaskCost');
    return 'using default backend mtcost';
}
function enableRequestBundling() {
    exports.config.areRequestsBundled = true;
    setItem('aggregation-requestBundling', true);
    return 'Aggregation requests are now bundled for the life of this session.';
}
function disableRequestBundling() {
    exports.config.areRequestsBundled = false;
    setItem('aggregation-requestBundling', false);
    return 'Aggregation requests are now unbundled.';
}
function enableRequestDebouncing() {
    exports.config.alwaysDebounceRequests = true;
    setItem('aggregation-requestDebouncing', true);
    return 'Aggregation requests are now debounced for the life of this session.';
}
function disableRequestDebouncing() {
    exports.config.alwaysDebounceRequests = false;
    setItem('aggregation-requestDebouncing', false);
    return 'Aggregation requests are no longer debounced.';
}
function enableTimingIds() {
    exports.config.disableTimingIds = false;
    setItem('aggregation-disableTimingIds', false);
    return 'Aggregation timing ids are now enabled for the life of this session.';
}
function disableTimingIds() {
    exports.config.disableTimingIds = true;
    setItem('aggregation-disableTimingIds', true);
    return 'Aggregation timing ids are now disabled for the life of this session.';
}
function enableNamesOnRequestUrls() {
    exports.config.addAggNamesToRequestUrls = true;
    setItem('aggregation-addAggNamesToRequestUrls', true);
    return 'Aggregation requests now also send agg names as part of their url for the life of this session.';
}
function disableNamesOnRequestUrls() {
    exports.config.addAggNamesToRequestUrls = false;
    setItem('aggregation-addAggNamesToRequestUrls', false);
    return 'Aggregation requests no longer send agg names as part of their url for the life of this session.';
}
function getItem(name) {
    try {
        return sessionStorage.getItem(name);
        // eslint-disable-next-line no-empty
    }
    catch { }
}
function setItem(name, value) {
    try {
        sessionStorage.setItem(name, value);
        // eslint-disable-next-line no-empty
    }
    catch { }
}
function removeItem(name) {
    try {
        sessionStorage.removeItem(name);
        // eslint-disable-next-line no-empty
    }
    catch { }
}
function generateTimingId() {
    return (0, uuid_1.v4)().slice(0, 8);
}
function getReportControlFields(report, request) {
    if (report.kind !== 'Workflow') {
        return report;
    }
    const subLocationType = (0, get_1.default)(request, 'responseConfig.subLocation', '');
    return report[Object.keys(report).find(key => key.toLowerCase() === `${subLocationType}reportcontrol`)];
}
